import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Trash2, Search, RotateCcw, X, Check, Loader2, Trash, RotateCcw as RestoreIcon } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { categoryStore } from '../../store/categoryStore';

interface Paper {
  id: string;
  title: string;
  year: number | null;
  month: number | null;
  organization: string | null;
  category_type: string | null;
  level_text: string | null;
  paper_type: string | null;
  duration: number | null;
  question_count: number | null;
  single_choice_count: number | null;
  multi_choice_count: number | null;
  judge_count: number | null;
  coding_count: number | null;
  total_score: number | null;
  remark: string | null;
  is_free: boolean;
  is_published: boolean;
  status: string;
  original_status: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string | null;
  user_id: string | null;
}

export default function RecycleBin() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);

  useEffect(() => {
    loadPapers();
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedPaper(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const loadPapers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('papers')
        .select('*')
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;
      setPapers((data || []) as unknown as Paper[]);
      setSelectedIds(new Set());
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === papers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(papers.map(p => p.id)));
    }
  };

  const restorePaper = async (paper: Paper) => {
    const targetStatus = paper.original_status || 'draft';
    const targetLabel = targetStatus === 'published' ? '试卷列表' : '草稿列表';
    if (!confirm(`确定恢复此试卷？将恢复到${targetLabel}`)) return;
    try {
      await supabase
        .from('papers')
        .update({
          status: targetStatus,
          deleted_at: null,
          original_status: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', paper.id);
      loadPapers();
      alert(`已恢复到${targetLabel}`);
    } catch (err) {
      
      alert('恢复失败');
    }
  };

  const permanentlyDelete = async (id: string) => {
    if (!confirm('确定彻底删除此试卷？此操作不可恢复！')) return;
    try {
      // 按顺序清理所有关联数据（避免外键约束冲突）
      // 每个表单独 try-catch，忽略不存在的表（404）
      const tablesToClean = [
        'exam_records',
        'python_submissions',
        'scratch_submissions',
        'teacher_assignments',
        'favorites',
        'wrong_questions',
        'questions'
      ];

      for (const table of tablesToClean) {
        try {
          await supabase.from(table).delete().eq('paper_id', id);
        } catch (e) {
          // 忽略 404 错误（表不存在或无权限）
          console.warn(`清理表 ${table} 失败，跳过:`, e);
        }
      }

      await supabase.from('papers').delete().eq('id', id);
      loadPapers();
      alert('已彻底删除');
    } catch (err) {

      alert('删除失败');
    }
  };

  const batchRestore = async () => {
    if (selectedIds.size === 0) {
      alert('请先选择要恢复的试卷');
      return;
    }
    if (!confirm(`确定恢复选中的 ${selectedIds.size} 份试卷？`)) return;
    try {
      for (const id of selectedIds) {
        const paper = papers.find(p => p.id === id);
        const targetStatus = paper?.original_status || 'draft';
        await supabase
          .from('papers')
          .update({
            status: targetStatus,
            deleted_at: null,
            original_status: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);
      }
      setSelectedIds(new Set());
      loadPapers();
      alert('批量恢复成功');
    } catch (err) {
      
      alert('批量恢复失败');
    }
  };

  const batchDelete = async () => {
    if (selectedIds.size === 0) {
      alert('请先选择要彻底删除的试卷');
      return;
    }
    if (!confirm(`确定彻底删除选中的 ${selectedIds.size} 份试卷？此操作不可恢复！`)) return;
    try {
      for (const id of selectedIds) {
        // 按顺序清理所有关联数据（避免外键约束冲突）
        const tablesToClean = [
          'exam_records',
          'python_submissions',
          'scratch_submissions',
          'teacher_assignments',
          'favorites',
          'wrong_questions',
          'questions'
        ];

        for (const table of tablesToClean) {
          try {
            await supabase.from(table).delete().eq('paper_id', id);
          } catch (e) {
            // 忽略 404 错误（表不存在或无权限）
            console.warn(`清理表 ${table} 失败，跳过:`, e);
          }
        }

        await supabase.from('papers').delete().eq('id', id);
      }
      setSelectedIds(new Set());
      loadPapers();
      alert('批量彻底删除成功');
    } catch (err) {

      alert('批量删除失败');
    }
  };

  const generateDisplayTitle = (paper: Paper) => {
    if (paper.title) return paper.title;
    // 如果没有title，根据字段动态生成
    const parts: string[] = [];
    if (paper.year) parts.push(`${paper.year}年`);
    if (paper.month) parts.push(`${paper.month}月`);
    if (paper.organization) parts.push(paper.organization);
    if (paper.category_type) {
      const categoryName = categoryStore.getCategoryNameByCode(paper.category_type) || paper.category_type;
      parts.push(categoryName);
    }
    if (paper.level_text) parts.push(paper.level_text);
    if (paper.paper_type) {
      parts.push(paper.paper_type === 'real' ? '真题' : '模拟题');
    }
    return parts.length > 0 ? parts.join('') : '未命名试卷';
  };

  const resetFilters = () => {
    setSearchQuery('');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const match = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2}):(\d{1,2})/);
    if (match) {
      return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')} ${match[4].padStart(2, '0')}:${match[5].padStart(2, '0')}:${match[6].padStart(2, '0')}`;
    }
    const dateOnly = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (dateOnly) {
      return `${dateOnly[1]}-${dateOnly[2].padStart(2, '0')}-${dateOnly[3].padStart(2, '0')}`;
    }
    return '-';
  };

  const getOriginalStatusTag = (originalStatus: string | null) => {
    if (originalStatus === 'published') {
      return <span className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full">原试卷列表</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">原草稿列表</span>;
  };

  const getAccessTag = (isFree: boolean) => {
    if (isFree) {
      return <span className="px-2 py-0.5 text-xs font-medium text-green-600 bg-green-50 rounded-full">免费</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-medium text-orange-600 bg-orange-50 rounded-full">付费</span>;
  };

  const filteredPapers = papers.filter(p => {
    if (searchQuery && !generateDisplayTitle(p).toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-6 bg-[#f0f7ff] min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">回收站</h1>
          <p className="text-gray-500 mt-1">管理已删除的试卷，支持恢复或彻底删除</p>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="搜索已删除试卷..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        {selectedIds.size > 0 && (
          <>
            <button
              onClick={batchRestore}
              className="px-4 py-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 flex items-center gap-2"
            >
              <RestoreIcon className="w-4 h-4" />
              批量恢复 ({selectedIds.size})
            </button>
            <button
              onClick={batchDelete}
              className="px-4 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-2"
            >
              <Trash className="w-4 h-4" />
              批量彻底删除 ({selectedIds.size})
            </button>
          </>
        )}
        <button
          onClick={resetFilters}
          className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          重置筛选
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : filteredPapers.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>回收站为空</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#e8f4ff]">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === papers.length && papers.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-blue-900">试卷名称</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">单选</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">多选</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">判断</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">编程</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">总分</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">时长</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">原位置</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">访问</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">删除时间</th>
                <th className="px-3 py-3 text-center text-sm font-medium text-blue-900">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPapers.map((paper) => (
                <motion.tr
                  key={paper.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(paper.id)}
                      onChange={() => toggleSelect(paper.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => setSelectedPaper(paper)}
                      className="text-sm text-gray-900 font-medium hover:text-blue-600 hover:underline cursor-pointer text-left"
                    >
                      {generateDisplayTitle(paper)}
                    </button>
                  </td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.single_choice_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.multi_choice_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.judge_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.coding_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.total_score || '-'}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.duration ? `${paper.duration}分钟` : '-'}</td>
                  <td className="px-2 py-3 text-center">{getOriginalStatusTag(paper.original_status)}</td>
                  <td className="px-2 py-3 text-center">{getAccessTag(paper.is_free)}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{formatDate(paper.deleted_at)}</td>
                  <td className="px-3 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => restorePaper(paper)}
                        className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded"
                        title="恢复"
                      >
                        恢复
                      </button>
                      <button
                        onClick={() => permanentlyDelete(paper.id)}
                        className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded"
                        title="彻底删除"
                      >
                        彻底删除
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {selectedPaper && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPaper(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">试卷详情</h2>
                  <button
                    onClick={() => setSelectedPaper(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">试卷名称</h3>
                    <p className="text-base text-gray-900">{generateDisplayTitle(selectedPaper)}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">年份</h3>
                      <p className="text-base text-gray-900">{selectedPaper.year || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">月份</h3>
                      <p className="text-base text-gray-900">{selectedPaper.month || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">考评体系</h3>
                      <p className="text-base text-gray-900">{selectedPaper.organization || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">课程分类</h3>
                      <p className="text-base text-gray-900">{selectedPaper.category_type || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">级别分类</h3>
                      <p className="text-base text-gray-900">{selectedPaper.level_text || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">类型</h3>
                      <p className="text-base text-gray-900">
                        {selectedPaper.paper_type === 'real' ? '真题' : selectedPaper.paper_type === 'mock' ? '模拟题' : '-'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">单选题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.single_choice_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">多选题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.multi_choice_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">判断题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.judge_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">编程题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.coding_count || 0}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">总分</h3>
                      <p className="text-base text-gray-900">{selectedPaper.total_score || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">时长</h3>
                      <p className="text-base text-gray-900">{selectedPaper.duration ? `${selectedPaper.duration}分钟` : '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">题目总数</h3>
                      <p className="text-base text-gray-900">{selectedPaper.question_count || 0}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">原位置</h3>
                      <div className="mt-1">{getOriginalStatusTag(selectedPaper.original_status)}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">访问权限</h3>
                      <div className="mt-1">{getAccessTag(selectedPaper.is_free)}</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">删除时间</h3>
                    <p className="text-base text-gray-900">{formatDate(selectedPaper.deleted_at)}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">备注</h3>
                    <p className="text-base text-gray-900">{selectedPaper.remark || '-'}</p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => restorePaper(selectedPaper)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <RestoreIcon className="w-4 h-4" />
                    恢复
                  </button>
                  <button
                    onClick={() => setSelectedPaper(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
